import { Link } from "react-router-dom";
import { ChevronRight, MapPin, Phone, Mail, Clock, AlertTriangle, Navigation, Car } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import AppointmentForm from "@/components/AppointmentForm";
import { SITE, waLink } from "@/lib/site";

const LOCAL_FAQS = [
  {
    q: "Where are Dr. Sanjay Teza's ENT clinics in Varanasi?",
    a: `Dr. Teza consults at two locations — the Dr. Sanjay Teza ENT Center in Bhelupur (evenings) and Vardaan Hospital on BHU Road, Newada (mornings). Full addresses and maps are above.`,
  },
  {
    q: "What are the consultation timings?",
    a: `Bhelupur ENT Center: ${SITE.clinics[0].timings}. Vardaan Hospital: ${SITE.clinics[1].timings}. Booking ahead on WhatsApp is recommended.`,
  },
  {
    q: "Is parking available at the clinics?",
    a: `${SITE.clinics[0].parking} At Vardaan Hospital, ${SITE.clinics[1].parking.toLowerCase()}`,
  },
  {
    q: "Do you accept walk-in patients?",
    a: "Yes, walk-ins are welcome during clinic hours. However, booking ahead via WhatsApp or the form helps reduce your waiting time.",
  },
];

export default function Contact() {
  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "@id": "https://entspecialistvaranasi.com/contact#page",
      url: "https://entspecialistvaranasi.com/contact",
      name: "Contact Dr. Sanjay Teza ENT Clinic",
      isPartOf: { "@id": "https://entspecialistvaranasi.com/#website" },
      about: [
        { "@id": "https://entspecialistvaranasi.com/#bhelupur-clinic" },
        { "@id": "https://entspecialistvaranasi.com/#vardaan-clinic" },
      ],
    },
    breadcrumbSchema([
      { name: "Home", url: "https://entspecialistvaranasi.com/" },
      { name: "Contact", url: "https://entspecialistvaranasi.com/contact" },
    ]),
  ];

  return (
    <>
      <Seo
        title="Contact Dr. Sanjay Teza — ENT Clinic in Varanasi | Appointments & Directions"
        description="Contact Dr. Sanjay Teza's ENT clinic in Varanasi. Address, phone, WhatsApp, clinic hours, directions, parking and Google Map. Book your ENT appointment today."
        path="/contact"
        schemas={schemas}
      />

      {/* Header */}
      <section className="pt-36 md:pt-44 pb-14 bg-brand-mint/25 border-b border-brand-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <nav className="flex items-center gap-2 text-xs text-brand-muted mb-8" data-testid="contact-breadcrumb">
              <Link to="/" className="hover:text-brand-pine">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-brand-ink">Contact</span>
            </nav>
            <p className="text-xs tracking-[0.2em] uppercase text-brand-pine mb-4">Get in Touch</p>
            <h1 className="font-heading font-bold text-brand-ink text-5xl md:text-6xl lg:text-7xl leading-[1.03] tracking-tight">
              Visit the clinic
            </h1>
            <p className="mt-6 text-lg text-brand-muted max-w-2xl leading-relaxed">
              Book an appointment, send a message on WhatsApp, or drop by during clinic hours. We&apos;re
              here to help you feel better.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Form + details */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <div className="bg-brand-surface border border-brand-line rounded-3xl p-8 md:p-12">
              <h2 className="font-heading text-3xl text-brand-ink mb-2">Request an appointment</h2>
              <p className="text-brand-muted text-sm mb-8">
                Fill the form and confirm instantly on WhatsApp — or use the direct chat button.
              </p>
              <AppointmentForm />
            </div>
          </Reveal>

          {/* Details */}
          <div className="lg:col-span-5 space-y-5">
            <Reveal className="bg-brand-surface border border-brand-line rounded-3xl p-8">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <span className="grid place-items-center w-11 h-11 rounded-full bg-brand-mint/50 text-brand-pine shrink-0">
                    <MapPin className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-xs tracking-[0.15em] uppercase text-brand-muted mb-1">Address</p>
                    <p className="text-brand-ink leading-relaxed">{SITE.addressFull}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <span className="grid place-items-center w-11 h-11 rounded-full bg-brand-mint/50 text-brand-pine shrink-0">
                    <Phone className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-xs tracking-[0.15em] uppercase text-brand-muted mb-1">Phone</p>
                    <a href={`tel:${SITE.phoneRaw}`} data-testid="contact-phone-link" className="text-brand-ink hover:text-brand-pine transition-colors">
                      {SITE.phoneDisplay}
                    </a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <span className="grid place-items-center w-11 h-11 rounded-full bg-brand-mint/50 text-brand-pine shrink-0">
                    <Mail className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-xs tracking-[0.15em] uppercase text-brand-muted mb-1">Email</p>
                    <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="text-brand-ink hover:text-brand-pine transition-colors">
                      {SITE.email}
                    </a>
                  </div>
                </div>
                <a
                  href={waLink(`Hello, I would like to book an appointment with ${SITE.doctorName}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp px-7 py-3.5 text-sm text-white transition-colors hover:bg-whatsapp-hover w-full"
                >
                  Chat on WhatsApp
                </a>
              </div>
            </Reveal>

            {/* Hours */}
            <Reveal delay={1} className="bg-brand-surface border border-brand-line rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-5">
                <Clock className="w-5 h-5 text-brand-pine" />
                <p className="text-xs tracking-[0.15em] uppercase text-brand-muted">Clinic Hours</p>
              </div>
              <ul className="divide-y divide-brand-line">
                {SITE.hours.map((h) => (
                  <li key={h.day} className="flex items-center justify-between py-3 text-[15px]">
                    <span className="text-brand-ink">{h.day}</span>
                    <span className="text-brand-muted">{h.time}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex items-start gap-3 rounded-xl bg-destructive/8 border border-destructive/20 p-4" data-testid="emergency-note">
                <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-brand-ink leading-relaxed">
                  <span className="font-semibold">Emergency?</span> For severe bleeding, sudden hearing
                  loss or breathing difficulty, please call{" "}
                  <a href={`tel:${SITE.phoneRaw}`} className="text-brand-pine underline underline-offset-4">
                    {SITE.phoneDisplay}
                  </a>{" "}
                  or visit the nearest hospital immediately.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Locations — two clinics */}
      <section className="py-16 md:py-24" data-testid="clinic-locations">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal className="mb-12">
            <p className="text-xs tracking-[0.2em] uppercase text-brand-pine mb-4">Our Locations</p>
            <h2 className="font-heading font-semibold text-brand-ink text-3xl md:text-4xl tracking-tight">
              Two clinics across Varanasi
            </h2>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-8">
            {SITE.clinics.map((c, idx) => (
              <Reveal
                key={c.name}
                delay={idx}
                className="bg-brand-surface border border-brand-line rounded-3xl overflow-hidden flex flex-col"
                data-testid={`clinic-card-${idx}`}
              >
                <div className="overflow-hidden border-b border-brand-line">
                  <iframe
                    title={`${c.name} location on Google Maps`}
                    data-testid={`google-map-${idx}`}
                    src={c.mapEmbed}
                    className="w-full h-[300px]"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <div className="p-8 flex flex-col flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-heading text-2xl text-brand-ink">{c.name}</h3>
                    <span className="text-[10px] tracking-[0.16em] uppercase text-brand-pine bg-brand-mint/50 rounded-full px-3 py-1">
                      {c.area}
                    </span>
                  </div>
                  <div className="mt-5 space-y-3 text-sm text-brand-muted flex-1">
                    <p className="flex gap-3">
                      <MapPin className="w-4 h-4 mt-0.5 text-brand-pine shrink-0" />
                      <span className="text-brand-ink leading-relaxed">{c.addressFull}</span>
                    </p>
                    <p className="flex gap-3">
                      <Clock className="w-4 h-4 mt-0.5 text-brand-pine shrink-0" />
                      <span className="text-brand-ink">{c.timings}</span>
                    </p>
                    <ul className="pl-7 space-y-1.5">
                      {c.landmarks.map((l) => (
                        <li key={l} className="flex items-start gap-2 leading-relaxed">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-pine shrink-0" />
                          {l}
                        </li>
                      ))}
                      <li className="flex items-start gap-2 leading-relaxed">
                        <Car className="w-3.5 h-3.5 mt-0.5 text-brand-pine shrink-0" />
                        {c.parking}
                      </li>
                    </ul>
                  </div>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <a
                      href={c.gbpLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`clinic-directions-link-${idx}`}
                      className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-5 py-2.5 text-sm text-brand-ink transition-colors hover:bg-brand-tealHover"
                    >
                      <Navigation className="w-4 h-4" /> Get directions
                    </a>
                    <a
                      href={waLink(`Hello, I would like to book an appointment at ${c.name}, Varanasi.`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`clinic-whatsapp-link-${idx}`}
                      className="inline-flex items-center gap-2 rounded-full border border-whatsapp px-5 py-2.5 text-sm text-whatsapp transition-colors hover:bg-whatsapp hover:text-white"
                    >
                      Book this clinic
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Local FAQs */}
      <section className="pb-24">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          <Reveal className="mb-8">
            <p className="text-xs tracking-[0.2em] uppercase text-brand-pine mb-4">Local FAQs</p>
            <h2 className="font-heading font-bold text-brand-ink text-3xl md:text-4xl tracking-tight">
              Visiting the clinic
            </h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full" data-testid="contact-faq-accordion">
              {LOCAL_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-brand-line">
                  <AccordionTrigger
                    data-testid={`contact-faq-trigger-${i}`}
                    className="text-left font-heading text-xl md:text-2xl text-brand-ink py-6 hover:no-underline"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-brand-muted pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
